// src/pages/StudentEvents.jsx
// Student / teacher signup menu (Phase 2). Public auth-only route (/events):
// any signed-in KA user can see open events and sign up. Capacity + waitlist
// are enforced server-side by the signupForEvent / cancelSignup functions.
// Mobile-first (students use phones). Not tool-gated.

import React, { useEffect, useState, useCallback } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  collection, query, where, onSnapshot, doc, getDoc,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../config/firebase";

const KA_RED = "#E83530";
const KA_NAVY = "#233C5F";
const KA_GOLD = "#A29164";

export default function StudentEvents() {
  const [user, setUser] = useState(undefined); // undefined=loading, null=signed out
  const [events, setEvents] = useState([]);
  const [mine, setMine] = useState({}); // eventId -> status
  const [busyId, setBusyId] = useState("");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), (u) => setUser(u || null));
    return () => unsub();
  }, []);

  // Not signed in -> route to the responsive login and come back.
  useEffect(() => {
    if (user === null) {
      window.location.assign(`/login?next=${encodeURIComponent("/events")}`);
    }
  }, [user]);

  // Live list of open events. Single-field equality only (no composite index) —
  // filter to signup-open and sort by start time in the browser.
  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, "events"), where("status", "==", "active"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((e) => e.signupOpen !== false)
          .sort((a, b) => {
            const am = a.startAt?.toMillis ? a.startAt.toMillis() : 0;
            const bm = b.startAt?.toMillis ? b.startAt.toMillis() : 0;
            return am - bm;
          });
        setEvents(list);
      },
      (e) => console.error("events list error", e)
    );
    return () => unsub();
  }, [user]);

  // Load my signup status for the visible events.
  const refreshMine = useCallback(async () => {
    if (!user || events.length === 0) return;
    const entries = await Promise.all(
      events.map(async (ev) => {
        try {
          const s = await getDoc(doc(db, `events/${ev.id}/signups/${user.uid}`));
          const st = s.exists() ? s.data().status : null;
          return [ev.id, st && st !== "cancelled" ? st : null];
        } catch {
          return [ev.id, null];
        }
      })
    );
    setMine(Object.fromEntries(entries));
  }, [user, events]);

  useEffect(() => { refreshMine(); }, [refreshMine]);

  const call = async (name, ev) => {
    setBusyId(ev.id); setMsg("");
    try {
      const fns = getFunctions(undefined, "us-central1");
      await httpsCallable(fns, name)({ eventId: ev.id });
      await refreshMine();
    } catch (e) {
      setMsg(e?.message || "Something went wrong.");
    } finally {
      setBusyId("");
    }
  };

  const fmt = (ts) => {
    try {
      const d = ts?.toDate ? ts.toDate() : new Date(ts);
      return d.toLocaleString("en-US", { timeZone: "Asia/Amman", weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    } catch { return ""; }
  };

  // Capacity counts students only. Teachers/staff always have a spot.
  const viewerIsStudent = /(\d{2})@/.test(user?.email || "");
  const studentsIn = (ev) => ev.studentCount ?? ev.signedUpCount ?? 0;
  const isFull = (ev) => viewerIsStudent && ev.capacity != null && studentsIn(ev) >= ev.capacity;
  const spots = (ev) => {
    if (!viewerIsStudent) return "Open sign-up";
    if (ev.capacity == null) return "Open sign-up";
    const left = Math.max(0, ev.capacity - studentsIn(ev));
    return left > 0 ? `${left} spot${left === 1 ? "" : "s"} left` : "Full — waitlist";
  };

  if (user === undefined) {
    return <div style={wrap}><div style={card}><p style={p}>Loading…</p></div></div>;
  }

  return (
    <div style={wrap}>
      <div style={{ maxWidth: 560, margin: "0 auto", width: "100%" }}>
        <div style={{ color: KA_RED, fontWeight: 700, fontSize: 13, letterSpacing: 1, margin: "4px 0 2px" }}>
          KING'S ACADEMY · TRIPS & EVENTS
        </div>
        <h2 style={{ color: KA_NAVY, margin: "0 0 4px" }}>Sign up</h2>
        <p style={{ color: "#666", marginTop: 0, fontSize: 14 }}>
          Choose a trip or event to join. If it's full you can take a waitlist spot and we'll move you up automatically.
        </p>

        {msg && <div style={{ color: KA_RED, fontSize: 14, margin: "8px 0" }}>{msg}</div>}

        {events.length === 0 && (
          <div style={{ ...card, textAlign: "center", color: "#888" }}>No open events right now.</div>
        )}

        <div style={{ display: "grid", gap: 12 }}>
          {events.map((ev) => {
            const status = mine[ev.id];
            const busy = busyId === ev.id;
            return (
              <div key={ev.id} style={card}>
                <div style={{ fontWeight: 700, color: KA_NAVY, fontSize: 17 }}>{ev.name}</div>
                {ev.description && <div style={{ color: "#555", fontSize: 14, margin: "4px 0" }}>{ev.description}</div>}
                <div style={{ color: "#888", fontSize: 13, margin: "6px 0" }}>
                  {fmt(ev.startAt)} → {fmt(ev.endAt)}
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10 }}>
                  <span style={{ fontSize: 13, color: status === "confirmed" ? "#1a7f37" : status === "waitlisted" ? KA_GOLD : "#666", fontWeight: 600 }}>
                    {status === "confirmed" ? "✓ You're in"
                      : status === "waitlisted" ? "⏳ Waitlisted"
                      : spots(ev)}
                  </span>
                  {status ? (
                    <button disabled={busy} onClick={() => call("cancelSignup", ev)} style={{ ...btn, background: "#fff", color: KA_RED, border: `1px solid ${KA_RED}` }}>
                      {busy ? "…" : "Cancel"}
                    </button>
                  ) : (
                    <button disabled={busy} onClick={() => call("signupForEvent", ev)} style={{ ...btn, background: busy ? "#aaa" : KA_RED, color: "#fff", border: "none" }}>
                      {busy ? "…" : (isFull(ev) ? "Join waitlist" : "Sign up")}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const wrap = { minHeight: "100vh", background: "#f4f5f7", padding: "24px 16px 48px" };
const card = { background: "#fff", borderRadius: 14, padding: "16px 18px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" };
const p = { color: "#555", fontSize: 15, margin: 0, textAlign: "center" };
const btn = { borderRadius: 10, padding: "10px 18px", fontSize: 14, fontWeight: 600, cursor: "pointer" };